import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Shield, Phone, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const ExitIntentPopup = () => {
  const { language } = useLanguage();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const location = useLocation();

  const hiddenPaths = ['/demander-un-pret-auto', '/verifier-admissibilite', '/admin', '/pret-auto-2e-chance', '/pret-auto-3e-chance'];
  const isHidden = hiddenPaths.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (isHidden || dismissed) return;
    if (sessionStorage.getItem('exitPopupShown')) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 5 && !show && !dismissed) {
        setShow(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 10000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isHidden, dismissed, show]);

  const close = () => {
    setShow(false);
    setDismissed(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    setSending(true);
    try {
      await axios.post('/api/leads/quick', {
        name: name.trim(),
        phone: phone.trim(),
        source: 'exit_intent_popup',
        page: location.pathname
      });
      setSent(true);
    } catch {
      setSent(true);
    }
    setSending(false);
  };

  if (!show || isHidden) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={close} data-testid="exit-intent-popup">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute top-3 right-3 text-white/70 hover:text-white z-10" data-testid="exit-popup-close">
          <X className="w-5 h-5" />
        </button>

        <div className="bg-gradient-to-r from-[#1a5c3b] to-[#16a085] px-6 py-5 text-center">
          <p className="text-green-200 text-xs font-medium tracking-wide uppercase mb-1">
            {language === 'en' ? 'Wait — before you go' : 'Attendez — avant de partir'}
          </p>
          <h2 className="text-xl font-bold text-white leading-tight">
            {language === 'en' ? 'Check your eligibility in 30 seconds' : 'Vérifiez votre admissibilité en 30 secondes'}
          </h2>
        </div>

        <div className="p-6">
          {sent ? (
            <div className="text-center py-4" data-testid="exit-popup-success">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <ArrowRight className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-1">
                {language === 'en' ? 'Thank you!' : 'Merci!'}
              </h3>
              <p className="text-gray-500 text-sm">
                {language === 'en' ? 'A specialist will contact you within 24 hours.' : 'Un spécialiste vous contactera dans les 24 heures.'}
              </p>
              <Button onClick={close} variant="outline" className="mt-4 text-sm">
                {language === 'en' ? 'Close' : 'Fermer'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Input
                  placeholder={language === 'en' ? 'Your name' : 'Votre nom'}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-11"
                  data-testid="exit-popup-name"
                />
              </div>
              <div>
                <Input
                  type="tel"
                  placeholder={language === 'en' ? 'Your phone number' : 'Votre numéro de téléphone'}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="h-11"
                  data-testid="exit-popup-phone"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-[#1a5c3b] hover:bg-[#165432] text-white font-semibold py-5 text-base"
                data-testid="exit-popup-submit"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'en' ? 'Sending...' : 'Envoi...'}</>
                ) : (
                  <>{language === 'en' ? 'Get a free callback' : 'Recevoir un appel gratuit'} <Phone className="ml-2 w-4 h-4" /></>
                )}
              </Button>
              <div className="flex items-center justify-center gap-1.5 mt-2">
                <Shield className="w-3 h-3 text-gray-400" />
                <span className="text-[11px] text-gray-400">
                  {language === 'en' ? 'No credit check • No obligation • 100% confidential' : 'Sans enquête de crédit • Sans obligation • 100% confidentiel'}
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
